import { motion } from "framer-motion";
import { C, Chip, Readout, Section, biasRGB, fmt, label } from "./shared";

/* ANALYTICS — the adaptive leaderboard and the instrument board, straight from the scoring loop. */
export const Analytics = ({ pulse }) => {
  const perf = pulse?.agents?.performance || {}, board = pulse?.agents?.leaderboard || [];
  const rows = board.length ? board : Object.keys(perf);
  const cons = pulse?.agents?.consensus, all = pulse?.desk?.consensus_all || {};
  return (
    <Section id="analytics" n="08" kicker="ANALYTICS" title={<>Every agent is graded.<br />Every grade changes its vote.</>}
      sub="Accuracy and calibration are measured on scored calls. Over-confident agents are discounted automatically; the consensus renormalises so the desk speaks with one weighted voice." testid="analytics">
      <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-[1.4fr_1fr]">
        <div data-testid="leaderboard">
          <div className="mb-4 flex items-center gap-2"><span className="font-mono text-[8px] tracking-[0.22em] text-[#7F8BA3]">AGENT LEADERBOARD</span><Chip rgb={C.violet}>ADAPTIVE WEIGHTS ON</Chip></div>
          <ol className="space-y-4">
            {rows.map((name, i) => {
              const p = perf[name] || {}, acc = p.directional_accuracy ?? 0, w = p.weight_multiplier ?? 1;
              return (
                <li key={name} className="grid grid-cols-[28px_1fr_auto] items-center gap-4" data-testid={`lb-row-${name}`}>
                  <span className="font-display text-lg font-black text-white/30">{i + 1}</span>
                  <div>
                    <div className="flex items-baseline justify-between font-mono text-[11px]"><span className="text-white">{label(name)}</span><span className="tabular-nums text-white/70">{p.samples ? `${acc}% · n=${p.samples}` : "warming up"}</span></div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                      <motion.div initial={{ width: 0 }} whileInView={{ width: `${acc}%` }} viewport={{ once: true }} transition={{ duration: 1.2, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }} className="h-full rounded-full" style={{ background: acc >= 50 ? "#00E5B5" : "#F5B942", boxShadow: `0 0 12px ${acc >= 50 ? "rgba(0,229,181,0.6)" : "rgba(245,185,66,0.5)"}` }} />
                    </div>
                    <div className="mt-1 flex gap-4 font-mono text-[9px] text-white/40"><span>calibration {p.confidence_calibration != null ? `${p.confidence_calibration > 0 ? "+" : ""}${p.confidence_calibration}` : "—"}</span><span>rank score {p.rank_score ?? "—"}</span></div>
                  </div>
                  <span className="font-mono text-[13px] font-semibold tabular-nums" style={{ color: w < 1 ? "#FF3355" : w > 1 ? "#00E5B5" : "#E6EDF7" }}>×{w.toFixed(2)}</span>
                </li>
              );
            })}
            {!rows.length && <li className="font-mono text-[10px] text-white/35">no scored calls yet</li>}
          </ol>
        </div>
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-2 gap-4">
            <Readout k="DESK ACCURACY" v={cons?.directional_accuracy != null ? `${cons.directional_accuracy}%` : "—"} rgb={cons?.directional_accuracy >= 50 ? C.emerald : C.amber} testid="an-desk-acc" />
            <Readout k="AVG CONFIDENCE" v={cons?.avg_confidence != null ? `${cons.avg_confidence}%` : "—"} />
          </div>
          <div>
            <div className="mb-3 font-mono text-[8px] tracking-[0.22em] text-[#7F8BA3]">INSTRUMENT BOARD · LIVE</div>
            <ul className="divide-y divide-white/5" data-testid="instrument-board">
              {(pulse?.symbols || []).map((s) => {
                const c = all[s.symbol];
                return (
                  <li key={s.symbol} className="grid grid-cols-[52px_1fr_auto_auto] items-center gap-3 py-2 font-mono text-[10px]" data-testid={`inst-${s.symbol}`}>
                    <span className="font-semibold text-white">{s.symbol}</span>
                    <span className="tabular-nums text-white/70">{fmt(s.price)} <span style={{ color: (s.change_pct || 0) >= 0 ? "#00E5B5" : "#FF3355" }}>{s.change_pct != null ? `${s.change_pct >= 0 ? "+" : ""}${s.change_pct}%` : ""}</span></span>
                    <span style={{ color: `rgb(${biasRGB(s.structure)})` }}>{s.structure || "—"}</span>
                    <span style={{ color: c ? `rgb(${biasRGB(c.direction)})` : "rgba(230,237,247,0.3)" }}>{c ? `${c.direction.toUpperCase()} ${Math.round(c.confidence)}%` : `score ${s.score ?? 0}`}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
};
